import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Barcode, AlertTriangle, Loader2, History, Package, Truck, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getAsset, getAssetHistory } from '../lib/api';
import JsBarcode from 'jsbarcode';

const StatusBadge = ({ status }) => {
  const styles = {
    available: 'badge-success',
    on_hire: 'badge-info',
    damaged: 'badge-error',
    maintenance: 'badge-warning',
  };
  return (
    <span className={`${styles[status] || 'badge-neutral'} inline-block text-sm`}>
      {status.replace('_', ' ')}
    </span>
  );
};

const eventIcons = {
  created: Package,
  hired: Truck,
  delivered: Truck,
  collected: CheckCircle,
  damaged: XCircle,
  repaired: CheckCircle,
};

const eventColors = {
  created: 'text-blue-500 bg-blue-50',
  hired: 'text-amber-500 bg-amber-50',
  delivered: 'text-green-500 bg-green-50',
  collected: 'text-blue-500 bg-blue-50',
  damaged: 'text-red-500 bg-red-50',
  repaired: 'text-green-500 bg-green-50',
};

export default function AssetDetailPage() {
  const { id } = useParams();
  const [asset, setAsset] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const barcodeRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [assetRes, historyRes] = await Promise.all([
          getAsset(id),
          getAssetHistory(id).catch(() => ({ data: [] }))
        ]);
        setAsset(assetRes.data);
        setHistory(historyRes.data);
      } catch (error) {
        console.error('Error fetching asset:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    if (asset && barcodeRef.current) {
      JsBarcode(barcodeRef.current, asset.asset_id, {
        format: 'CODE128',
        width: 2,
        height: 80,
        displayValue: true,
        fontSize: 14,
        margin: 10,
      });
    }
  }, [asset]);

  const handlePrintBarcode = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Barcode - ${asset.asset_id}</title>
          <style>
            body { font-family: sans-serif; text-align: center; padding: 20px; }
            h2 { margin-bottom: 10px; }
          </style>
        </head>
        <body>
          <h2>${asset.name}</h2>
          <p>${asset.asset_id}</p>
          <svg id="barcode"></svg>
          <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
          <script>
            JsBarcode("#barcode", "${asset.asset_id}", { format: "CODE128", width: 2, height: 100, displayValue: true });
            setTimeout(() => window.print(), 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
        <h2 className="font-heading font-bold text-xl">Asset Not Found</h2>
        <Link to="/assets" className="text-primary hover:underline mt-2 inline-block">
          Back to Assets
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="asset-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/assets">
          <Button variant="outline" size="icon" className="border-2 border-slate-200">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="font-heading font-black text-3xl text-slate-900 uppercase tracking-tight">
            {asset.name}
          </h1>
          <p className="font-mono text-slate-500">{asset.asset_id}</p>
        </div>
        <StatusBadge status={asset.status} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm">
                Asset Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Category</p>
                  <p className="font-body font-semibold">{asset.category}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Make & Model</p>
                  <p className="font-body font-semibold">{asset.make} {asset.model || '-'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Serial Number</p>
                  <p className="font-mono">{asset.serial_number || '-'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Location</p>
                  <p className="font-body font-semibold">{asset.location || '-'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Current Hirer</p>
                  <p className="font-body font-semibold">{asset.current_hirer || '-'}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Next Test Due</p>
                  <p className={`font-body font-semibold ${
                    asset.next_test_due && new Date(asset.next_test_due) < new Date() 
                      ? 'text-red-600' 
                      : ''
                  }`}>
                    {asset.next_test_due || '-'}
                  </p>
                </div>
              </div>
              {asset.description && (
                <div className="mt-6 pt-6 border-t border-slate-100">
                  <p className="font-mono text-xs uppercase text-slate-400 mb-1">Description</p>
                  <p className="font-body text-slate-600">{asset.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Photo */}
          <Card className="border-2 border-slate-200 shadow-industrial overflow-hidden">
            <img
              src={asset.photo_url || 'https://images.unsplash.com/photo-1632095710940-ad578e8cbe6b?w=400'}
              alt={asset.name}
              className="w-full h-48 object-cover"
            />
          </Card>

          {/* Barcode */}
          <Card className="border-2 border-slate-200 shadow-industrial">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
                <Barcode className="w-4 h-4" />
                Barcode
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 flex flex-col items-center">
              <svg ref={barcodeRef}></svg>
              <Button 
                onClick={handlePrintBarcode} 
                variant="outline" 
                className="mt-4 w-full border-2 border-slate-200"
                data-testid="print-barcode-btn"
              >
                Print Barcode
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Asset History */}
      <Card className="border-2 border-slate-200 shadow-industrial">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="font-heading font-bold uppercase text-sm flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            Asset History
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {history.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <History className="w-10 h-10 mx-auto mb-2" />
              <p>No history records yet</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {history.map((entry) => {
                const IconComponent = eventIcons[entry.event_type] || Package;
                const colorClass = eventColors[entry.event_type] || 'text-slate-500 bg-slate-50';
                return (
                  <div key={entry.id} className="flex items-start gap-4 p-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-body font-semibold text-slate-800">{entry.description}</p>
                      <div className="flex items-center gap-2 mt-1 text-sm text-slate-500">
                        <span className="font-mono text-xs uppercase">{entry.event_type}</span>
                        <span>•</span>
                        <span>{entry.performed_by}</span>
                        {entry.hire_order_id && (
                          <>
                            <span>•</span>
                            <Link to={`/hire-orders/${entry.hire_order_id}`} className="text-primary hover:underline">
                              {entry.hire_order_id}
                            </Link>
                          </>
                        )}
                      </div>
                      <p className="font-mono text-xs text-slate-400 mt-1">
                        {new Date(entry.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
